import { useId, useRef, useState, type ReactNode } from 'react';
import { Button } from './basics';

/**
 * A place to drop files, with a button for people who'd rather choose them.
 * The input stays in the page (visually hidden) so it works with keyboards,
 * screen readers and tests.
 */
export function FileDrop({ label, accept, multiple = false, disabled = false, hint, onFiles, compact = false }: {
  /** The button text, which also names the file input. */
  label: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  onFiles: (files: File[]) => void;
  /** A slim bar instead of a tall drop area. */
  compact?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const take = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <div
      className="el-drop"
      data-over={over || undefined}
      data-compact={compact || undefined}
      data-disabled={disabled || undefined}
      onDragOver={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) take(e.dataTransfer.files); }}
    >
      <input
        ref={input}
        id={id}
        type="file"
        className="el-vh"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => { take(e.target.files); e.target.value = ''; }}
      />
      <Button size="sm" disabled={disabled} onClick={() => input.current?.click()}>{label}</Button>
      <span className="el-hint">{hint ?? (multiple ? 'or drop files here' : 'or drop a file here')}</span>
    </div>
  );
}

/** 1.2 MB, 340 KB, 900 bytes */
export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} bytes`;
}
